import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { apiUrl } from '@/consts/route';

/**
 * env별 RUM 수집/녹화 비율.
 * - sessionSampleRate: 전체 세션 중 RUM 수집 비율 (과금 단위)
 * - sessionReplaySampleRate: 수집된 세션 중 Session Replay 녹화 비율 (sessionSampleRate에 상대적, Replay 단가가 가장 높음)
 *   → 실효 Replay 비율 = sessionSampleRate × sessionReplaySampleRate / 100
 *
 * production만 의미있게 수집하고 비프로덕션(dev/SB/local)은 녹화를 끄고 표본만 수집해 Datadog 사용량을 절감한다.
 */
const RUM_SAMPLING: Record<
  keyof typeof apiUrl,
  { sessionSampleRate: number; sessionReplaySampleRate: number }
> = {
  production: { sessionSampleRate: 33, sessionReplaySampleRate: 10 }, // 2026-06 사용량 1/3 절감 지시. 실효 Replay 3.3%
  test: { sessionSampleRate: 100, sessionReplaySampleRate: 10 },
  development: { sessionSampleRate: 10, sessionReplaySampleRate: 0 }, // SB: 표본만, Replay off
  dev: { sessionSampleRate: 10, sessionReplaySampleRate: 0 },
  local: { sessionSampleRate: 0, sessionReplaySampleRate: 0 }, // 로컬은 수집 안 함
};

export function initDatadogRum() {
  // RUM 비활성화 kill-switch. 기본 OFF — 켜려는 환경에서만 NEXT_PUBLIC_DATADOG_RUM_ENABLED=true 명시.
  if (process.env.NEXT_PUBLIC_DATADOG_RUM_ENABLED !== 'true') return;

  const env = (process.env.NEXT_PUBLIC_ENV ?? 'development') as keyof typeof apiUrl;
  const { sessionSampleRate, sessionReplaySampleRate } =
    RUM_SAMPLING[env] ?? RUM_SAMPLING.development;

  datadogRum.init({
    applicationId: 'e9c3a449-50d9-4d3b-9fb3-aede6aa10f52',
    clientToken: 'pub0aed02422c1040a02fa4da5bd0760497',
    site: 'datadoghq.com',
    service: 'cmong-fe',
    env,
    version: process.env.NEXT_PUBLIC_APP_VERSION ?? '0.0.0',
    sessionSampleRate,
    sessionReplaySampleRate,
    defaultPrivacyLevel: 'allow',
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: [apiUrl[env]],
    plugins: [reactPlugin({ router: false })],
  });
}

/**
 * 세션 유저 식별.
 * - 로그인된 모든 유저(kakao/email/…)에 대해 호출하여 `@usr.id`로 검색 가능하게 함
 * - 호출 이후의 RUM 이벤트에만 적용되므로 세션 초기화 직후 호출 권장
 */
export function tagDatadogUser(user: { user_id: string; email?: string; auth_method?: string }) {
  datadogRum.setUser({
    id: user.user_id,
    email: user.email,
    auth_method: user.auth_method,
  });
  if (user.auth_method) {
    datadogRum.setGlobalContextProperty('auth_method', user.auth_method);
  }
}

/** 로그아웃 시 유저 정보 제거 */
export function clearDatadogUser() {
  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('auth_method');
}

/**
 * Kakao OAuth 실패 기록.
 * - signIn()은 실패 시 throw하지 않고 `{ error }`로 resolve하므로 자동 캡처되지 않음
 * - 실패 케이스만 호출하면 됨
 */
export function trackKakaoLoginError(error: string, status?: number) {
  datadogRum.addError(new Error(`Kakao login failed: ${error}`), { status });
}
